import React, { useState } from 'react';
import { Info, X } from 'lucide-react';

// What each ReachOut role can do and see — shown from the (i) icon in
// Administration so admins can check before assigning. Keep in step with
// utils/roleAccess.js (menus) and backend util/TenantAccess.java (data rules).
const ROLES = [
  {
    name: 'Admin',
    color: '#1B3A4B',
    can: [
      'Administration: add, edit and remove any user in the company',
      'Assign any roles, including several to one person (and extra roles to themselves)',
      "Edit company-wide Rules & Permissions (and every team's rules)",
      'Dashboard and Analytics',
    ],
    cannot: ['Hiring tools (Craft JD, Stack Ranking, ReachOut, Format Resume) — add the Manager role for these'],
    data: 'Sees and can change every position and candidate in the company and its branches.',
  },
  {
    name: 'Manager',
    color: '#B8860B',
    can: [
      'All hiring tools: Craft JD, Stack Ranking, ReachOut, Format Resume',
      'Add Recruiters to their team',
      'Dashboard, Analytics, Settings and Wizetok link',
    ],
    cannot: ['Add Admins or Managers', 'Change company-wide rules (they can tighten rules for their own team)'],
    data: 'Sees and can change their own positions, positions they are assigned to, and those of the people they added (their team). Can assign, lock and pin positions.',
  },
  {
    name: 'Recruiter',
    color: '#22A06B',
    can: [
      'All hiring tools: Craft JD, Stack Ranking, ReachOut, Format Resume',
      'Add Viewers',
      'Dashboard, Analytics, Settings and Wizetok link',
    ],
    cannot: ['Add Admins, Managers or Recruiters'],
    data: 'Sees and can change their own positions and positions assigned to them as Editor; positions assigned as Viewer are read-only.',
  },
  {
    name: 'Viewer',
    color: '#6B7280',
    can: ['Open the hiring tools in read-only mode'],
    cannot: ['Create, edit, upload or delete anything', 'Administration'],
    data: 'Read-only; sees only their own positions.',
  },
];

export default function RolePermissionsInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="What can each role do?"
        aria-label="Role permissions"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#0041C2', padding: 2, display: 'inline-flex', verticalAlign: 'middle' }}
      >
        <Info size={17} />
      </button>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Role permissions"
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, maxWidth: 860, width: '100%', maxHeight: '88vh', overflowY: 'auto', padding: '20px 22px', color: '#111827' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>ReachOut role permissions</h2>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 14px' }}>
              A person can hold several roles; they get everything each role allows (e.g. Admin + Manager =
              Administration plus the hiring tools). Features also need to be enabled for your company by the Service Provider.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
              {ROLES.map((r) => (
                <div key={r.name} style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 12 }}>
                  <span style={{ display: 'inline-block', fontSize: 12, fontWeight: 700, color: '#fff', background: r.color, borderRadius: 4, padding: '2px 8px' }}>{r.name}</span>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#15803D', margin: '10px 0 4px' }}>CAN</div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, lineHeight: 1.5 }}>
                    {r.can.map((c) => <li key={c}>{c}</li>)}
                  </ul>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#B91C1C', margin: '10px 0 4px' }}>CANNOT</div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, lineHeight: 1.5 }}>
                    {r.cannot.map((c) => <li key={c}>{c}</li>)}
                  </ul>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', margin: '10px 0 4px' }}>DATA</div>
                  <div style={{ fontSize: 12, lineHeight: 1.5 }}>{r.data}</div>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 12, color: '#6B7280', margin: '14px 0 0' }}>
              These are the defaults. Admins and managers can change them in <strong>Rules &amp; Permissions</strong>
              (for example &quot;Data visibility: Company wide&quot; lets everyone <em>view</em> all of the company&apos;s data).
            </p>
          </div>
        </div>
      )}
    </>
  );
}
